import React, { useState } from 'react';

import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

import { CONTENT, CONTENT_FIELDS, IMAGES, PACKAGES } from '~/consts/offerFields';

import OffersService from '../../../services/OffersService';
import ErrorDisplay from '../../Common/ErrorDisplay';

const ALLOWED_COPY_OPTIONS = [CONTENT, PACKAGES, IMAGES];

const INITIAL_STATE = {
  copyOfferId: '',
  hasError: false,
  errorMessage: '',
  selectedCopyOptions: [CONTENT],
};

function CopyOfferModal(props) {
  const [state, setState] = useState(INITIAL_STATE);

  const hideCopyModal = () => {
    setState(INITIAL_STATE);
    props.hideCopyModal();
  };

  /* additional fields to be added per request
  - we do not copy "slug", because it will autogenerate on "save"
  */
  const getEditableOfferDetailsWithOptions = (copiedOffer, selectedCopyOptions) => {
    const editableOfferDetails = {};
    let editableFields = [];
    selectedCopyOptions.forEach((option) => {
      if (option === CONTENT) {
        editableFields = editableFields.concat(CONTENT_FIELDS);
      } else if (copiedOffer[option]) {
        editableFields.push(option);
      }
    });
    for (const field of editableFields) {
      if (field === PACKAGES) {
        editableOfferDetails[field] = copyPackagesBasedOnName(copiedOffer[field]);
      } else {
        editableOfferDetails[field] = copiedOffer[field];
      }
    }
    return editableOfferDetails;
  };

  const copyPackagesBasedOnName = (candidatePackagesToCopy) => {
    const originalPackages = props.originalOfferDetails?.packages;
    const packagesToUpdate = [];
    for (const originalPackage of originalPackages) {
      const packageToCopy = candidatePackagesToCopy.find(
        (candidatePackage) => candidatePackage.deal_option_name === originalPackage.deal_option_name,
      );
      if (packageToCopy) {
        packagesToUpdate.push({
          ...originalPackage,
          name: packageToCopy.name,
          description: packageToCopy.description,
          inclusion_highlights: packageToCopy.inclusion_highlights,
          room_occupancy: packageToCopy.room_occupancy,
          regions: packageToCopy.regions,
        });
      }
    }
    if (!packagesToUpdate.length) {
      alert('No package was copied since no shared deal option name was detected.');
    }
    return packagesToUpdate;
  };

  const toggleCopyOption = (event) => {
    const optionTriggered = event.target.name;
    setState((prevState) => ({
      ...prevState,
      selectedCopyOptions: prevState.selectedCopyOptions.includes(optionTriggered)
        ? prevState.selectedCopyOptions.filter((option) => option !== optionTriggered)
        : prevState.selectedCopyOptions.concat(optionTriggered),
    }));
  };

  const checkOffersCompatibility = (copiedOffer, currentOfferStatus) => {
    let areOffersCompatible = true;
    let errorMessage = '';

    if (copiedOffer.status !== 'content-approved') {
      areOffersCompatible = false;
      errorMessage = 'The offer you want to copy is not content-approved';
    }

    if (areOffersCompatible && currentOfferStatus === 'content-approved') {
      areOffersCompatible = false;
      errorMessage = "You can't overwrite the details of a content-approved offer";
    }

    return {
      areOffersCompatible,
      errorMessage,
    };
  };

  const searchAndCopyOfferDetails = async (event) => {
    event.preventDefault();

    // remove old errors if any
    setState((prevState) => ({
      ...prevState,
      hasError: false,
      errorMessage: '',
    }));

    const currentCopyOfferId = state.copyOfferId.trim();
    const currentSelectedOptions = state.selectedCopyOptions;

    const offerData = await OffersService.getOffer(currentCopyOfferId);
    if (!offerData) {
      setState((prevState) => ({
        ...prevState,
        hasError: true,
        errorMessage: 'There is no such offer',
      }));
      return;
    }

    const check = checkOffersCompatibility(offerData.result, props.currentOfferStatus);

    if (!check.areOffersCompatible) {
      setState((prevState) => ({
        ...prevState,
        hasError: true,
        errorMessage: check.errorMessage,
      }));
      return;
    }

    props.setAsNotReady();

    // get and pass just the details that are editable
    const editableOfferDetails = getEditableOfferDetailsWithOptions(offerData.result, currentSelectedOptions);
    props.copyOfferData(editableOfferDetails, currentSelectedOptions);
  };

  const handleChange = (event) => {
    setState((prevState) => ({ ...prevState, copyOfferId: event.target.value }));
  };

  return (
    <Dialog open={props.isCopyModalVisible} onClose={hideCopyModal}>
      <DialogTitle>Copy editable details from another content-approved offer</DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Typography variant="subtitle2">
          Please note that this action will pre-fill the details and will let you review and save the result. If you
          have data in any of the editable fields, it will be overwritten.
        </Typography>

        <form onSubmit={searchAndCopyOfferDetails}>
          <FormGroup>
            {state.hasError && <ErrorDisplay message={state.errorMessage} />}
            <Grid container spacing={2} alignItems="center">
              <Grid
                size={{
                  sm: 'grow',
                }}
              >
                <TextField
                  type="text"
                  placeholder="Type offer ID here"
                  value={state.copyOfferId}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={state.selectedCopyOptions.length === 0}
                >
                  Copy
                </Button>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              {ALLOWED_COPY_OPTIONS.map((option) => (
                <Grid
                  key={option}
                  size={{
                    sm: 4,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={option}
                        id={`cp-option-${option}`}
                        checked={state.selectedCopyOptions.includes(option)}
                        onChange={toggleCopyOption}
                      />
                    }
                    label={`Copy ${option}`}
                  />
                </Grid>
              ))}
            </Grid>
            {state.selectedCopyOptions.includes(IMAGES) &&
              props.originalOfferDetails?.images &&
              props.originalOfferDetails.images.length > 0 && (
                <Alert severity="warning">
                  This offer currently has images, continuing will <strong>replace </strong> these with the copied
                  images
                </Alert>
              )}
          </FormGroup>
        </form>
      </DialogContent>
      <DialogActions>
        <Stack justifyContent="end">
          <Button variant="contained" color="primary" onClick={hideCopyModal}>
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default CopyOfferModal;
